// sql.js + IndexedDB 桥接 · R28 严守 db.exec 字符串拼接
// 注: v19 client 端用 sql.js · server 端 D1 通过 Worker API

// sql.js 通过 index.html script tag 加载 (window.initSqlJs)
// 真根因: vite ESM bundler 误处理 sql.js UMD · 反复 'is not a function'
// 修法: 直接 CDN script · 跳过 npm 解析 · 业界标准 sql.js 用法
import type { Database } from 'sql.js'
declare global {
  interface Window {
    initSqlJs: (config: { locateFile?: (file: string) => string }) => Promise<any>
  }
}
import { openDB, type IDBPDatabase } from 'idb'
import { DEMO_EMPLOYEES, DEMO_CUSTOMERS, DEMO_PRODUCTS_1812812, DEMO_SALES_ORDER_1812812 } from './demo-seed'

let db: Database | null = null
let idb: IDBPDatabase | null = null

const IDB_NAME = 'trungnam-mes-v19'
const IDB_STORE = 'sqlite'
const IDB_KEY = 'main'

async function getIdb(): Promise<IDBPDatabase> {
  if (idb) return idb
  idb = await openDB(IDB_NAME, 1, {
    upgrade(database) {
      if (!database.objectStoreNames.contains(IDB_STORE)) {
        database.createObjectStore(IDB_STORE)
      }
    }
  })
  return idb
}

export async function initDb(): Promise<Database> {
  if (db) return db
  // 等 window.initSqlJs 加载 (script tag async load · 最多等 3 秒)
  for (let i = 0; i < 30 && !window.initSqlJs; i++) {
    await new Promise(r => setTimeout(r, 100))
  }
  if (!window.initSqlJs) {
    throw new Error('sql.js script 未加载 · 检查 index.html script tag + CSP')
  }
  const SQL = await window.initSqlJs({
    locateFile: (file: string) => `https://cdn.jsdelivr.net/npm/sql.js@1.10.3/dist/${file}`
  })
  const i = await getIdb()
  const saved = await i.get(IDB_STORE, IDB_KEY)
  db = saved ? new SQL.Database(new Uint8Array(saved as ArrayBuffer)) : new SQL.Database()
  await ensureSchemaAndSeed()
  return db as Database
}

export function getDb(): Database {
  if (!db) throw new Error('Database not initialized · call initDb() first')
  return db
}

// R28 严守: 用 db.exec 字符串拼接 (不用 db.run with params)
export function escSql(s: string | number | null | undefined): string {
  if (s === null || s === undefined) return 'NULL'
  if (typeof s === 'number') return String(s)
  return "'" + String(s).replace(/'/g, "''") + "'"
}

export function exec(sql: string): unknown[] {
  return getDb().exec(sql)
}

export async function saveDbToIdb(): Promise<void> {
  if (!db) return
  const data = db.export()
  const i = await getIdb()
  await i.put(IDB_STORE, data.buffer, IDB_KEY)
}

/**
 * 启动时建表 + auto-seed demo 数据 (灯塔阶段)
 * 已 seed 则 skip · 后续不重复
 */
async function ensureSchemaAndSeed(): Promise<void> {
  if (!db) return
  // 1. 建表 (employees / customers / products / sales_orders / work_orders 等)
  db.exec(`
    CREATE TABLE IF NOT EXISTS employees (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      code TEXT UNIQUE NOT NULL,
      name_cn TEXT, name_vi TEXT, role TEXT, dept TEXT,
      password TEXT, email TEXT, phone TEXT,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP
    );
    CREATE TABLE IF NOT EXISTS customers (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      code TEXT UNIQUE NOT NULL,
      name_cn TEXT, name_vi TEXT, credit_limit REAL,
      payment_terms TEXT, country TEXT, deleted_at TEXT
    );
    CREATE TABLE IF NOT EXISTS products (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      code TEXT UNIQUE NOT NULL,
      name_cn TEXT, name_vi TEXT,
      inner_length REAL, inner_width REAL, inner_height REAL,
      base_price REAL, is_active INTEGER DEFAULT 1
    );
    CREATE TABLE IF NOT EXISTS sales_orders (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      code TEXT UNIQUE,
      customer_id INTEGER, product_id INTEGER, work_order_id INTEGER,
      total_amount REAL, status TEXT, qty INTEGER,
      delivery_date TEXT, notes TEXT,
      is_urgent INTEGER DEFAULT 0,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP
    );
    CREATE TABLE IF NOT EXISTS work_orders (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      code TEXT UNIQUE,
      product_id INTEGER, planned_qty INTEGER, completed_qty INTEGER,
      status TEXT, start_date TEXT, end_date TEXT
    );
    CREATE TABLE IF NOT EXISTS purchase_orders (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      code TEXT, total_amount REAL, status TEXT,
      notes TEXT, remark TEXT
    );
  `)

  // 2. seed 检查: 已有数据则 skip
  const empCheck = db.exec(`SELECT COUNT(*) FROM employees`) as any[]
  const empCount = empCheck[0]?.values?.[0]?.[0] || 0
  if (empCount > 0) {
    console.info(`[v19 DB] employees 已 seed ${empCount} 条 · skip`)
    return
  }

  console.info('[v19 DB] 首次启动 · seed demo 数据...')

  // 3. seed employees (90 人)
  for (const e of DEMO_EMPLOYEES) {
    db.exec(`INSERT OR IGNORE INTO employees (code, name_cn, name_vi, role, dept, password) VALUES (${escSql(e.code)}, ${escSql(e.name_cn)}, ${escSql(e.name_vi)}, ${escSql(e.role)}, ${escSql(e.dept)}, ${escSql(e.password)})`)
  }

  // 4. seed customers (8 家)
  for (const c of DEMO_CUSTOMERS) {
    db.exec(`INSERT OR IGNORE INTO customers (code, name_cn, name_vi, credit_limit, payment_terms, country) VALUES (${escSql(c.code)}, ${escSql(c.name_cn)}, ${escSql(c.name_vi)}, ${c.credit}, ${escSql(c.terms)}, ${escSql(c.country)})`)
  }

  // 5. seed 1812812 产品 (3 SKU)
  for (const p of DEMO_PRODUCTS_1812812) {
    db.exec(`INSERT OR IGNORE INTO products (code, name_cn, name_vi, inner_length, inner_width, inner_height, base_price) VALUES (${escSql(p.code)}, ${escSql(p.name_cn)}, ${escSql(p.name_vi)}, ${p.length}, ${p.width}, ${p.height}, ${p.unit_price})`)
  }

  // 6. seed 1812812 销售订单
  const so = DEMO_SALES_ORDER_1812812
  db.exec(`INSERT OR IGNORE INTO sales_orders (code, total_amount, status, qty, delivery_date, notes) VALUES (${escSql(so.code)}, ${so.total_amount}, ${escSql(so.status)}, ${so.qty}, ${escSql(so.delivery_date)}, ${escSql(so.notes)})`)

  // 7. 持久化到 IDB (下次刷新仍在)
  await saveDbToIdb()
  console.info(`[v19 DB] seed 完成 · ${DEMO_EMPLOYEES.length} 员工 / ${DEMO_CUSTOMERS.length} 客户 / 1812812 完整链路`)
}
